/*
 * <product-reels> — Instagram-style video reels for the product page.
 *
 * Two responsibilities:
 *   1. Lazily autoplay muted/looping preview videos in a horizontal reels track
 *      (IntersectionObserver, gated by data-autoplay + prefers-reduced-motion).
 *   2. On card click, open the chosen video CENTERED in a lightbox modal with
 *      sound + controls, closable via ✕ / backdrop / Esc, with focus management.
 *
 * Reuses the theme's GLOBAL helpers when present (guarded by `typeof`):
 *   trapFocus / removeTrapFocus / window.pauseAllMedia (see assets/global.js).
 * We roll our own modal (not <modal-dialog>) on purpose: ModalDialog's
 * `media-modal` mode closes on a pointerup whose target isn't inside
 * `deferred-media, product-model`, which would close on a bare <video> click.
 *
 * House style: scoped custom element (plan 001) — guard the define, init in
 * connectedCallback, scope EVERY query to `this`, clean up in
 * disconnectedCallback so theme-editor re-renders and multiple instances are safe.
 */
if (!customElements.get('product-reels')) {
  customElements.define(
    'product-reels',
    class ProductReels extends HTMLElement {
      connectedCallback() {
        // Idempotent: connectedCallback can fire more than once (moves, editor
        // re-render). Guard against binding listeners / observers twice.
        if (this._initialized) return;
        this._initialized = true;

        // Cache scoped refs (everything relative to `this` — never document-wide).
        this.track = this.querySelector("[data-reels-track]");
        this.triggers = Array.from(this.querySelectorAll("[data-reels-trigger]"));
        this.modal = this.querySelector("[data-reels-modal]");
        this.overlay = this.querySelector("[data-reels-overlay]");
        this.closeButton = this.querySelector("[data-reels-close]");
        this.stage = this.querySelector("[data-reels-stage]");
        this.dialog = this.querySelector(".product-reels__dialog");

        // The modal is position:fixed. If any ancestor establishes a containing
        // block (a transform/filter from scroll-reveal animations, the theme
        // editor, etc.) the fixed child anchors to that ancestor instead of the
        // viewport and can be clipped/offset. Relocate the modal to <body> so it
        // always covers the viewport — mirroring the theme's own ModalDialog.
        if (this.modal && this.modal.parentElement !== document.body) {
          document.body.appendChild(this.modal);
          this._modalRelocated = true;
        }

        // Config.
        this.autoplay = this.dataset.autoplay === "true";
        this.reducedMotion =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        // Modal close-transition timing (kept in sync with the CSS fade ~200ms).
        this.closeDelay = 200;

        // State.
        this.opener = null; // the trigger button that opened the modal
        this.isOpen = false;
        this.inView = new Set(); // preview <video>s currently ≥ threshold visible
        this._closeTimer = null;
        this._rafId = null;
        this._tooltip = null; // body-level caption tooltip (created lazily)
        this._tooltipTrigger = null; // the card the tooltip is currently shown for

        // Bind handlers once so we can add/remove the exact same references.
        this._onTriggerClick = this._onTriggerClick.bind(this);
        this._onCloseClick = this._onCloseClick.bind(this);
        this._onModalClick = this._onModalClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onTriggerEnter = this._onTriggerEnter.bind(this);
        this._onTriggerLeave = this._onTriggerLeave.bind(this);
        this._onTooltipReposition = this._onTooltipReposition.bind(this);

        this._bindTriggers();
        this._bindModal();
        this._setupObserver();
      }

      disconnectedCallback() {
        // Make theme-editor re-render + element moves safe: tear everything down.
        if (this._io) {
          this._io.disconnect();
          this._io = null;
        }
        if (this._closeTimer) {
          clearTimeout(this._closeTimer);
          this._closeTimer = null;
        }
        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = null;
        }
        document.removeEventListener("keydown", this._onKeydown);

        if (this.triggers) {
          this.triggers.forEach((trigger) => {
            trigger.removeEventListener("click", this._onTriggerClick);
            trigger.removeEventListener("mouseenter", this._onTriggerEnter);
            trigger.removeEventListener("mouseleave", this._onTriggerLeave);
            trigger.removeEventListener("focusin", this._onTriggerEnter);
            trigger.removeEventListener("focusout", this._onTriggerLeave);
          });
        }
        if (this.closeButton)
          this.closeButton.removeEventListener("click", this._onCloseClick);
        if (this.modal)
          this.modal.removeEventListener("click", this._onModalClick);

        // Tear down the body-level caption tooltip + its scroll/resize listeners.
        this._teardownTooltip();

        // If torn down mid-open, don't leave the page scroll-locked.
        document.body.classList.remove("overflow-hidden");

        // Remove the modal we relocated to <body> so editor re-renders don't
        // leave an orphaned/duplicate modal behind.
        if (
          this._modalRelocated &&
          this.modal &&
          this.modal.parentElement === document.body
        ) {
          this.modal.remove();
          this._modalRelocated = false;
        }

        if (this.inView) this.inView.clear();
        this._initialized = false;
      }

      /* ---------------------------------------------------------------- *
       * Wiring
       * ---------------------------------------------------------------- */

      _bindTriggers() {
        this.triggers.forEach((trigger) => {
          trigger.addEventListener("click", this._onTriggerClick);
          // Caption tooltip: show on hover/focus, hide on leave/blur. focusin/
          // focusout (vs focus/blur) so keyboard tabbing onto the card shows it.
          trigger.addEventListener("mouseenter", this._onTriggerEnter);
          trigger.addEventListener("mouseleave", this._onTriggerLeave);
          trigger.addEventListener("focusin", this._onTriggerEnter);
          trigger.addEventListener("focusout", this._onTriggerLeave);
        });
      }

      _bindModal() {
        if (this.closeButton)
          this.closeButton.addEventListener("click", this._onCloseClick);
        // Close on any click that isn't on the video itself — this covers the
        // dark backdrop AND any dialog area around the video, which is more
        // reliable than listening on the overlay element alone.
        if (this.modal)
          this.modal.addEventListener("click", this._onModalClick);
      }

      /* ---------------------------------------------------------------- *
       * Lazy autoplay previews
       * ---------------------------------------------------------------- */

      _setupObserver() {
        // No autoplay when disabled, reduced-motion, or the API is unavailable:
        // the poster / play-icon simply stays put. Clicks still work either way.
        if (!this.autoplay || this.reducedMotion) return;
        if (typeof IntersectionObserver !== "function") return;

        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                this.inView.add(video);
                // Don't fight the open modal for audio focus / playback.
                if (!this.isOpen) this._playPreview(video);
              } else {
                this.inView.delete(video);
                this._pausePreview(video);
              }
            });
          },
          { threshold: 0.5 }
        );

        this.triggers.forEach((trigger) => {
          const video = trigger.querySelector("video");
          if (!video) return; // a card with no <video> is skipped, not fatal.
          this._io.observe(video);
        });
      }

      _playPreview(video) {
        if (!video) return;
        // Defensive: ensure preview stays silent / inline / looping regardless
        // of what the media_tag filter emitted.
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        const media = video.closest("[data-reels-media]");
        if (media) media.classList.add("is-playing");
        const playing = video.play();
        if (playing && typeof playing.catch === "function") playing.catch(() => {});
      }

      _pausePreview(video) {
        if (!video) return;
        video.pause();
        const media = video.closest("[data-reels-media]");
        if (media) media.classList.remove("is-playing");
      }

      _pauseAllPreviews() {
        this.triggers.forEach((trigger) => {
          const video = trigger.querySelector("video");
          this._pausePreview(video);
        });
      }

      /* ---------------------------------------------------------------- *
       * Caption tooltip (body-level, never clipped)
       *
       * The in-card .product-reels__caption span is visually hidden (CSS) and
       * only carries the text; on hover/focus we mirror it into a single
       * <body>-level .product-reels__tooltip positioned from the card's rect, so
       * a long caption wraps to multiple lines and is never clipped by the
       * horizontal-scroll track. Mirrors the modal-relocation pattern above.
       * ---------------------------------------------------------------- */

      _captionFor(trigger) {
        if (!trigger) return "";
        // Prefer the visible-text source; fall back to the aria-label, which the
        // markup also sets to the caption.
        const span = trigger.querySelector(".product-reels__caption");
        const text = (span && span.textContent) || trigger.getAttribute("aria-label") || "";
        return text.trim();
      }

      _ensureTooltip() {
        if (this._tooltip) return this._tooltip;
        const el = document.createElement("div");
        el.className = "product-reels__tooltip";
        // Decorative duplicate of the trigger's accessible name — hide from AT.
        el.setAttribute("aria-hidden", "true");
        el.hidden = true;
        document.body.appendChild(el);
        this._tooltip = el;
        return el;
      }

      _onTriggerEnter(event) {
        this._showTooltip(event.currentTarget);
      }

      _onTriggerLeave(event) {
        // Only hide if we're leaving the trigger the tooltip belongs to (guards
        // against a stale leave after focus moved to another card).
        if (!this._tooltipTrigger || event.currentTarget === this._tooltipTrigger) {
          this._hideTooltip();
        }
      }

      _showTooltip(trigger) {
        if (!trigger || this.isOpen) return; // don't show over an open modal
        const text = this._captionFor(trigger);
        if (!text) return; // captionless card → no tooltip (matches old behavior)

        const tip = this._ensureTooltip();
        tip.textContent = text;
        tip.hidden = false;
        this._tooltipTrigger = trigger;

        // Keep the pill aligned as the card scrolls / the viewport resizes.
        window.addEventListener("scroll", this._onTooltipReposition, true);
        window.addEventListener("resize", this._onTooltipReposition);

        this._positionTooltip(trigger);
        // Reveal on the next frame so the fade-in transition can run.
        if (this.reducedMotion) {
          tip.classList.add("is-visible");
        } else {
          requestAnimationFrame(() => {
            if (this._tooltipTrigger === trigger && this._tooltip) {
              this._tooltip.classList.add("is-visible");
            }
          });
        }
      }

      _hideTooltip() {
        window.removeEventListener("scroll", this._onTooltipReposition, true);
        window.removeEventListener("resize", this._onTooltipReposition);
        this._tooltipTrigger = null;
        if (!this._tooltip) return;
        this._tooltip.classList.remove("is-visible");
        this._tooltip.classList.remove("product-reels__tooltip--above");
        this._tooltip.hidden = true;
      }

      _onTooltipReposition() {
        if (this._tooltipTrigger) this._positionTooltip(this._tooltipTrigger);
      }

      _positionTooltip(trigger) {
        const tip = this._tooltip;
        if (!tip || !trigger) return;

        const card = trigger.getBoundingClientRect();
        const gap = 6; // px between card edge and pill (~0.6rem)
        const margin = 8; // px min clearance from the viewport edges
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;

        // Measure the pill (it's already in the DOM, un-hidden, with its text).
        const tw = tip.offsetWidth;
        const th = tip.offsetHeight;

        const cardCenterX = card.left + card.width / 2;

        // Horizontal: center on the card, then clamp inside the viewport.
        let left = cardCenterX - tw / 2;
        left = Math.max(margin, Math.min(left, vw - tw - margin));

        // Vertical: prefer below the card; flip above if it would overflow the
        // bottom and there's more room up top.
        const spaceBelow = vh - card.bottom;
        const spaceAbove = card.top;
        const placeAbove = spaceBelow < th + gap + margin && spaceAbove > spaceBelow;

        let top;
        if (placeAbove) {
          top = card.top - th - gap;
          tip.classList.add("product-reels__tooltip--above");
        } else {
          top = card.bottom + gap;
          tip.classList.remove("product-reels__tooltip--above");
        }

        // Point the arrow at the card center even when the pill is clamped.
        const arrowLeft = Math.max(8, Math.min(cardCenterX - left, tw - 8));
        tip.style.setProperty("--reels-tooltip-arrow-left", `${arrowLeft}px`);
        tip.style.left = `${Math.round(left)}px`;
        tip.style.top = `${Math.round(top)}px`;
      }

      _teardownTooltip() {
        window.removeEventListener("scroll", this._onTooltipReposition, true);
        window.removeEventListener("resize", this._onTooltipReposition);
        this._tooltipTrigger = null;
        if (this._tooltip) {
          this._tooltip.remove();
          this._tooltip = null;
        }
      }

      /* ---------------------------------------------------------------- *
       * Modal: open
       * ---------------------------------------------------------------- */

      _onTriggerClick(event) {
        const trigger = event.currentTarget;
        const source = trigger.querySelector("video");
        if (!source || !this.modal || !this.stage) return; // skip, don't throw.
        this.open(trigger, source);
      }

      open(trigger, source) {
        // The card was just clicked — drop its hover caption so the tooltip
        // doesn't linger over the opening modal.
        this._hideTooltip();

        // Pause inline previews first (so audio doesn't overlap), THEN stage the
        // modal clone. Order matters: the global pauseAllMedia() pauses EVERY
        // <video> on the page, so it must run before we play the clone.
        this._pauseAllPreviews();
        if (typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia();
        }
        // pauseAllMedia may not cover dynamically-added videos in every theme
        // version — re-pause our own tracked previews directly, belt-and-suspenders.
        this._pauseAllPreviews();

        // Deep-clone the card's <video> and promote it to a full playback clip:
        // sound on, controls + loop, autoplaying, with any inline sizing stripped
        // so the CSS can center + fit it to the viewport.
        const clone = source.cloneNode(true);
        clone.muted = false;
        clone.controls = true;
        clone.loop = true;
        clone.playsInline = true;
        clone.autoplay = true;
        clone.removeAttribute("muted");
        clone.removeAttribute("width");
        clone.removeAttribute("height");
        clone.style.removeProperty("width");
        clone.style.removeProperty("height");

        this.stage.replaceChildren(clone);
        // load() picks up the freshly-changed attributes (muted/controls) cleanly.
        if (typeof clone.load === "function") clone.load();
        const playing = clone.play();
        if (playing && typeof playing.catch === "function") playing.catch(() => {});

        // Reveal, then add the open class on the next frame so CSS can transition.
        this.modal.removeAttribute("hidden");
        this.isOpen = true;
        document.body.classList.add("overflow-hidden");

        if (this.reducedMotion) {
          this.modal.classList.add("is-open");
        } else {
          this._rafId = requestAnimationFrame(() => {
            this.modal.classList.add("is-open");
          });
        }

        // Escape-to-close while the modal is open.
        document.addEventListener("keydown", this._onKeydown);

        // Focus management: reuse the global focus trap when available.
        this.opener = trigger;
        if (typeof trapFocus === "function" && this.dialog) {
          trapFocus(this.dialog);
        } else if (this.dialog) {
          this.dialog.focus();
        }
      }

      /* ---------------------------------------------------------------- *
       * Modal: close
       * ---------------------------------------------------------------- */

      _onCloseClick() {
        this.close();
      }

      _onModalClick(event) {
        // Ignore clicks on the video (and its native controls); close otherwise.
        if (!event.target.closest("[data-reels-stage]")) {
          this.close();
        }
      }

      _onKeydown(event) {
        const key = event.key || event.code;
        if (key === "Escape" || key === "Esc") {
          event.preventDefault();
          this.close();
        }
      }

      close() {
        if (!this.isOpen || !this.modal) return;
        this.isOpen = false;

        // Stop + free the staged video so its audio dies immediately.
        if (this.stage) {
          const staged = this.stage.querySelector("video");
          if (staged) staged.pause();
          this.stage.replaceChildren();
        }

        // Drop document-level listeners and any pending raf right away.
        document.removeEventListener("keydown", this._onKeydown);
        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = null;
        }

        this.modal.classList.remove("is-open");
        document.body.classList.remove("overflow-hidden");

        const hide = () => {
          if (this.modal) this.modal.setAttribute("hidden", "");
          this._closeTimer = null;
        };
        if (this._closeTimer) clearTimeout(this._closeTimer);
        if (this.reducedMotion) {
          // No fade to wait for — hide immediately.
          hide();
        } else {
          // Let the CSS fade-out play, then re-hide.
          this._closeTimer = setTimeout(hide, this.closeDelay);
        }

        // Restore focus to the opener via the global helper when present.
        if (typeof removeTrapFocus === "function") {
          removeTrapFocus(this.opener);
        } else if (this.opener) {
          this.opener.focus();
        }
        this.opener = null;

        // Resume any previews still in view (observer keeps inView current).
        if (this.autoplay && !this.reducedMotion) {
          this.inView.forEach((video) => this._playPreview(video));
        }
      }
    }
  );
}
